/**
 * Home Page Component
 * Purpose: Clean, Google-like landing page with URL shortening functionality
 * Features: Minimalist design, powerful functionality, responsive layout
 * Author: Traklet App
 * Created: 2025
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Snackbar,
  Grid,
  Fade,
  Chip,
  Stack,
} from '@mui/material';
import {
  Link as LinkIcon,
  ContentCopy,
  Analytics,
  TrendingUp,
  Public,
  Shield,
  Speed,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { urlApi, apiUtils } from '../utils/api';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [longUrl, setLongUrl] = useState('');
  const [customAlias, setCustomAlias] = useState('');
  const [shortenedUrl, setShortenedUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showResult, setShowResult] = useState(false);

  const handleShortenUrl = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!longUrl.trim()) {
      setError('Please enter a URL to shorten');
      return;
    }

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await urlApi.shorten(longUrl.trim(), customAlias.trim() || undefined);
      
      if (response.success && response.data) {
        setShortenedUrl(response.data.short_url);
        setSuccess('URL shortened successfully!');
        setShowResult(true);
        setLongUrl('');
        setCustomAlias('');
      } else {
        setError(response.message || 'Failed to shorten URL');
      }
    } catch (error) {
      setError(apiUtils.getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shortenedUrl);
      setSuccess('URL copied to clipboard!');
    } catch (error) {
      setError('Failed to copy URL');
    }
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  // Clean, minimal design - no complex feature arrays needed

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Section - Google-like clean design */}
      <Container maxWidth="md" sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: { xs: 4, md: 8 } }}>
        <Box textAlign="center" sx={{ mb: { xs: 6, md: 8 } }}>
          {/* Main Logo/Title */}
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
              fontWeight: 300,
              color: '#1a73e8',
              mb: 3,
              letterSpacing: '-0.02em',
            }}
          >
            Traklet
          </Typography>
          
          {/* Simple tagline */}
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ 
              mb: 2, 
              fontWeight: 400,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.5
            }}
          >
            Shorten URLs. Track clicks. Analyze performance.
          </Typography>
          
          {/* Free registration message */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ 
              mb: 6,
              fontSize: '0.875rem',
              fontWeight: 400,
              opacity: 0.8
            }}
          >
            Free to register • No paywalls • Start shortening instantly
          </Typography>
          
          {/* Trust indicators */}
          <Stack 
            direction="row" 
            spacing={2} 
            justifyContent="center" 
            flexWrap="wrap"
            sx={{ mb: 6 }}
          >
            <Chip 
              icon={<Shield />} 
              label="Secure" 
              variant="outlined" 
              size="small"
              sx={{ borderColor: '#34d399', color: '#059669' }}
            />
            <Chip 
              icon={<Speed />} 
              label="Fast" 
              variant="outlined" 
              size="small"
              sx={{ borderColor: '#60a5fa', color: '#2563eb' }}
            />
            <Chip 
              icon={<Analytics />} 
              label="Analytics" 
              variant="outlined" 
              size="small"
              sx={{ borderColor: '#a78bfa', color: '#7c3aed' }}
            />
          </Stack>

          {/* URL Shortening Form - Google-like search box */}
          <Box component="form" onSubmit={handleShortenUrl} sx={{ maxWidth: 600, mx: 'auto' }}>
            <Paper
              elevation={1}
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: '24px',
                border: '1px solid #dadce0',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                },
                '&:focus-within': {
                  boxShadow: '0 2px 8px rgba(26,115,232,0.25)',
                  borderColor: '#1a73e8',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <LinkIcon sx={{ ml: 3, color: 'text.secondary' }} />
              <TextField
                fullWidth
                placeholder="Enter a long URL to shorten"
                value={longUrl}
                onChange={(e) => setLongUrl(e.target.value)}
                variant="standard"
                disabled={isLoading}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    px: 2,
                    py: 1.5,
                    fontSize: '16px',
                    '& input': {
                      '&::placeholder': {
                        color: '#9aa0a6',
                        opacity: 1,
                      },
                    },
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading || !longUrl.trim()}
                sx={{
                  mr: 1,
                  borderRadius: '20px',
                  backgroundColor: '#1a73e8',
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 3,
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#1557b0',
                  },
                  '&:disabled': {
                    backgroundColor: '#f8f9fa',
                    color: '#9aa0a6',
                  },
                }}
              >
                {isLoading ? 'Shortening...' : 'Shorten'}
              </Button>
            </Paper>
            
            {/* Custom alias option - collapsible */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <TextField
                size="small"
                label="Custom alias (optional)"
                placeholder="my-link"
                value={customAlias}
                onChange={(e) => setCustomAlias(e.target.value)}
                variant="outlined"
                disabled={isLoading}
                sx={{
                  maxWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>
          </Box>

          {/* Result Display */}
          <Fade in={showResult}>
            <Box sx={{ mt: 4, maxWidth: 600, mx: 'auto' }}>
              {shortenedUrl && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    backgroundColor: '#f8f9fa',
                    borderRadius: '12px',
                    border: '1px solid #e8eaed',
                  }}
                >
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 1, fontSize: '14px' }}
                  >
                    Your shortened URL:
                  </Typography>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      flexWrap: 'wrap'
                    }}
                  >
                    <Typography
                      component="a"
                      href={shortenedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#1a73e8',
                        textDecoration: 'none',
                        fontSize: '16px',
                        fontWeight: 500,
                        flex: 1,
                        minWidth: 0,
                        wordBreak: 'break-all',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {shortenedUrl}
                    </Typography>
                    <Button
                      onClick={handleCopyToClipboard}
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopy />}
                      sx={{
                        borderRadius: '20px',
                        textTransform: 'none',
                        borderColor: '#dadce0',
                        color: '#5f6368',
                        '&:hover': {
                          backgroundColor: '#f8f9fa',
                          borderColor: '#1a73e8',
                          color: '#1a73e8',
                        },
                      }}
                    >
                      Copy
                    </Button>
                  </Box>
                </Paper>
              )}
            </Box>
          </Fade>

          {!isAuthenticated && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mb: 3, fontSize: '14px' }}
              >
                Sign in to start shortening URLs
              </Typography>
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="center"
                sx={{ maxWidth: 400, mx: 'auto' }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: '#dadce0',
                    color: '#1a73e8',
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                      borderColor: '#1a73e8',
                    },
                  }}
                >
                  Sign In
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/register')}
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                    fontWeight: 500,
                    backgroundColor: '#1a73e8',
                    '&:hover': {
                      backgroundColor: '#1557b0',
                    },
                  }}
                >
                  Create Account
                </Button>
              </Stack>
            </Box>
          )}
      </Box>

        {/* Simple Features Section */}
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={4} textAlign="center">
              <TrendingUp sx={{ fontSize: 32, color: '#1a73e8', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track clicks and performance
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} textAlign="center">
              <Public sx={{ fontSize: 32, color: '#1a73e8', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                Global
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Works anywhere, anytime
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} textAlign="center">
              <Shield sx={{ fontSize: 32, color: '#1a73e8', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
                Secure
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Safe and reliable links
              </Typography>
            </Grid>
          </Grid>
        </Container>

        {/* Footer */}
        <Box sx={{ py: 4, textAlign: 'center', borderTop: '1px solid #e8eaed', mt: 8 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
            © 2025 Traklet. Simple, fast, and secure URL shortening.
          </Typography>
        </Box>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? 'error' : 'success'}
          variant="filled"
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Home;